import React from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Input,
  Select,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { StatusBadge } from '../../../components/ui';

/** Theme-aware Hub tokens (work in light + dark via semantic colors). */
export const hub = {
  muted: 'text.secondary',
  text: 'text.primary',
  cardBg: 'bg.surface',
  cardBorder: 'border.subtle',
  softBg: 'bg.input',
  pageBg: 'bg.canvas',
  ghostBorder: 'border.subtle',
  ghostHover: 'bg.input',
  thumbBg: 'bg.input',
  docEmptyBg: 'bg.input',
};

export function HubPageHeader({ title, subtitle, actions }) {
  return (
    <Flex
      align={{ base: 'stretch', md: 'flex-start' }}
      justify="space-between"
      gap={4}
      mb={5}
      flexWrap="wrap"
    >
      <Box minW={0} flex="1">
        <Heading size="lg" color="text.primary" letterSpacing="-0.02em" mb={1}>
          {title}
        </Heading>
        {subtitle ? (
          <Text color={hub.muted} fontSize="14px" maxW="720px">
            {subtitle}
          </Text>
        ) : null}
      </Box>
      {actions ? (
        <Flex gap={2} flexWrap="wrap" flexShrink={0}>
          {actions}
        </Flex>
      ) : null}
    </Flex>
  );
}

export function HubCard({ label, value, hint, onClick, icon: Icon }) {
  return (
    <Box
      as="button"
      textAlign="left"
      w="100%"
      h="100%"
      bg={hub.cardBg}
      border="1px solid"
      borderColor={hub.cardBorder}
      borderRadius="16px"
      px={5}
      py={5}
      cursor="pointer"
      transition="border-color 0.15s, transform 0.15s, background 0.15s"
      _hover={{ borderColor: 'utkarsh.amber', transform: 'translateY(-2px)' }}
      onClick={onClick}
    >
      <Flex align="flex-start" justify="space-between" gap={3} mb={2}>
        <Text
          fontSize="11px"
          fontWeight="700"
          letterSpacing="0.06em"
          textTransform="uppercase"
          color={hub.muted}
          pt={0.5}
        >
          {label}
        </Text>
        {Icon ? (
          <Flex
            align="center"
            justify="center"
            w="40px"
            h="40px"
            borderRadius="12px"
            bg="bg.amberTint"
            color="utkarsh.amber"
            flexShrink={0}
          >
            <Icon size={22} />
          </Flex>
        ) : null}
      </Flex>
      <Heading size="lg" color="text.primary" letterSpacing="-0.03em" mb={1}>
        {value}
      </Heading>
      <Text fontSize="13px" color={hub.muted}>
        {hint}
      </Text>
    </Box>
  );
}

export function HubSectionLabel({ children }) {
  return (
    <Text
      fontSize="11px"
      fontWeight="700"
      letterSpacing="0.08em"
      textTransform="uppercase"
      color={hub.muted}
      mb={3}
    >
      {children}
    </Text>
  );
}

export function HubPanel({ title, children, actions }) {
  return (
    <Box
      bg={hub.cardBg}
      border="1px solid"
      borderColor={hub.cardBorder}
      borderRadius="16px"
      p={{ base: 4, md: 5 }}
      h="100%"
    >
      {(title || actions) && (
        <Flex align="center" justify="space-between" gap={3} mb={3} flexWrap="wrap">
          {title ? (
            <Text fontWeight="600" fontSize="15px" color="text.primary">
              {title}
            </Text>
          ) : (
            <Box />
          )}
          {actions}
        </Flex>
      )}
      {children}
    </Box>
  );
}

export function HubRow({ label, children }) {
  return (
    <Flex
      justify="space-between"
      gap={4}
      py={2.5}
      borderBottom="1px solid"
      borderColor={hub.cardBorder}
      fontSize="14px"
      align="flex-start"
    >
      <Text color={hub.muted} flexShrink={0} minW="120px">
        {label}
      </Text>
      <Box textAlign="right" color={hub.text} wordBreak="break-word">
        {children ?? '—'}
      </Box>
    </Flex>
  );
}

export function HubAmberButton({ children, ...props }) {
  return (
    <Button
      size="sm"
      bg="utkarsh.amber"
      color="utkarsh.ink"
      fontWeight="600"
      _hover={{ bg: 'brand.600' }}
      _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
      {...props}
    >
      {children}
    </Button>
  );
}

export function HubGhostButton({ children, ...props }) {
  return (
    <Button
      size="sm"
      variant="outline"
      borderColor={hub.ghostBorder}
      color={hub.text}
      _hover={{ bg: hub.ghostHover }}
      _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
      {...props}
    >
      {children}
    </Button>
  );
}

export function HubFilterBar({ search, setSearch, searchPlaceholder, filters, onClear, extra }) {
  return (
    <Flex gap={2} flexWrap="wrap" mb={4} align="center">
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={searchPlaceholder}
        maxW={{ base: '100%', md: '300px' }}
        bg="bg.input"
        borderColor="border.subtle"
        color="text.primary"
        _placeholder={{ color: 'text.placeholder' }}
        _hover={{ borderColor: 'border.subtle' }}
        _focus={{ borderColor: 'utkarsh.amber', boxShadow: 'none' }}
        size="sm"
      />
      {(filters || []).map((f) => (
        <Select
          key={f.key}
          value={f.value}
          onChange={(e) => f.onChange(e.target.value)}
          maxW="180px"
          size="sm"
          bg="bg.input"
          borderColor="border.subtle"
          color="text.primary"
          _hover={{ borderColor: 'border.subtle' }}
          _focus={{ borderColor: 'utkarsh.amber', boxShadow: 'none' }}
        >
          {f.options.map((o) => (
            <option key={o.value} value={o.value} style={{ color: '#111' }}>
              {o.label}
            </option>
          ))}
        </Select>
      ))}
      {extra}
      {onClear ? (
        <HubGhostButton onClick={onClear}>Clear filters</HubGhostButton>
      ) : null}
    </Flex>
  );
}

export function HubTable({ columns, rows, onRowClick, loading }) {
  if (loading) {
    return (
      <Flex
        justify="center"
        align="center"
        py={16}
        border="1px solid"
        borderColor={hub.cardBorder}
        borderRadius="16px"
        bg={hub.cardBg}
      >
        <Spinner color="utkarsh.amber" />
      </Flex>
    );
  }

  return (
    <Box
      border="1px solid"
      borderColor={hub.cardBorder}
      borderRadius="16px"
      overflow="hidden"
      bg={hub.cardBg}
      w="100%"
    >
      <Box overflowX="auto">
        <Box as="table" w="100%" fontSize="14px" sx={{ borderCollapse: 'collapse' }}>
          <Box as="thead">
            <Box as="tr">
              {columns.map((c) => (
                <Box
                  as="th"
                  key={c.key}
                  textAlign="left"
                  px={4}
                  py={3}
                  fontSize="11px"
                  textTransform="uppercase"
                  letterSpacing="0.04em"
                  color={hub.muted}
                  borderBottom="1px solid"
                  borderColor={hub.cardBorder}
                  bg={hub.softBg}
                  whiteSpace="nowrap"
                >
                  {c.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box as="tbody">
            {rows.length === 0 ? (
              <Box as="tr">
                <Box
                  as="td"
                  colSpan={columns.length}
                  px={4}
                  py={10}
                  color={hub.muted}
                  textAlign="center"
                >
                  No rows match your filters.
                </Box>
              </Box>
            ) : (
              rows.map((row) => (
                <Box
                  as="tr"
                  key={row.id}
                  cursor={onRowClick ? 'pointer' : 'default'}
                  _hover={onRowClick ? { bg: hub.softBg } : undefined}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map((c) => (
                    <Box
                      as="td"
                      key={c.key}
                      px={4}
                      py={3}
                      borderBottom="1px solid"
                      borderColor={hub.cardBorder}
                      verticalAlign="middle"
                      color="text.primary"
                    >
                      {c.render ? c.render(row) : row[c.key] ?? '—'}
                    </Box>
                  ))}
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export function HubThumb({ src, label }) {
  if (!src) {
    return (
      <Flex
        w="44px"
        h="44px"
        borderRadius="8px"
        bg={hub.thumbBg}
        align="center"
        justify="center"
        flexShrink={0}
      >
        <Text fontSize="10px" color={hub.muted}>
          N/A
        </Text>
      </Flex>
    );
  }
  return (
    <Image
      src={src}
      alt={label || ''}
      w="44px"
      h="44px"
      objectFit="cover"
      borderRadius="8px"
      flexShrink={0}
      bg={hub.thumbBg}
    />
  );
}

export function kycBadgeProps(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'verified') return { status: 'verified' };
  if (s === 'rejected') return { status: 'error' };
  return { status: 'pending' };
}

export function HubKycBadge({ status, children }) {
  return (
    <StatusBadge {...kycBadgeProps(status)}>{children || status || 'Pending'}</StatusBadge>
  );
}

export function HubDocTile({ label, url, onOpen }) {
  const has = Boolean(url);
  return (
    <Box
      as="button"
      type="button"
      textAlign="left"
      border="1px solid"
      borderColor={hub.cardBorder}
      borderRadius="12px"
      overflow="hidden"
      bg={hub.docEmptyBg}
      cursor={has ? 'pointer' : 'default'}
      opacity={has ? 1 : 0.55}
      onClick={() => has && onOpen?.()}
      w="100%"
      _hover={has ? { borderColor: 'utkarsh.amber' } : undefined}
    >
      {has ? (
        <Image src={url} alt={label} w="100%" h="120px" objectFit="cover" />
      ) : (
        <Flex h="120px" align="center" justify="center">
          <Text fontSize="12px" color={hub.muted}>
            No file
          </Text>
        </Flex>
      )}
      <Text px={3} py={2} fontSize="12px" fontWeight="600" color={hub.text}>
        {label}
      </Text>
    </Box>
  );
}

export function HubDocRow({ label, url, onOpen }) {
  return (
    <Flex
      align="center"
      justify="space-between"
      gap={3}
      py={2.5}
      borderBottom="1px solid"
      borderColor={hub.cardBorder}
    >
      <Text fontSize="14px" color={hub.text}>
        {label}
      </Text>
      {url ? (
        <HubGhostButton onClick={onOpen}>View</HubGhostButton>
      ) : (
        <Text fontSize="12px" color={hub.muted}>
          Missing
        </Text>
      )}
    </Flex>
  );
}

export function HubLoading() {
  return (
    <Flex justify="center" align="center" minH="40vh">
      <VStack spacing={3}>
        <Spinner color="utkarsh.amber" size="lg" />
        <Text color={hub.muted} fontSize="14px">
          Loading…
        </Text>
      </VStack>
    </Flex>
  );
}

export function HubError({ message, onRetry }) {
  return (
    <HubPanel>
      <Text color="status.error" mb={3}>
        {message || 'Something went wrong'}
      </Text>
      {onRetry ? <HubGhostButton onClick={onRetry}>Retry</HubGhostButton> : null}
    </HubPanel>
  );
}
